import * as React from 'react';
import { cx } from '../utils';
import type { StyledProps } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

/* MetricTint — audit #9 rename (2026-05-16). Matches the v5 semantic
   palette (`--color-{tint}-*` token series). No `neutral` because an
   untinted metric simply omits the prop. */
export type MetricTint = 'theme' | 'success' | 'error' | 'warning' | 'info';
export type MetricSize = 'large' | 'small';

/* Bounded rest-prop allow-list (codex Card r1 lesson). */
type AllowedRestKey = 'id' | 'title' | `data-${string}` | `aria-${string}` | 'lang' | 'dir';
type AllowedRest = Partial<Record<AllowedRestKey, unknown>>;
const ALLOWED_LITERAL_KEYS = new Set<string>(['id', 'title', 'lang', 'dir']);
function pickAllowed(rest: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(rest)) {
    if (ALLOWED_LITERAL_KEYS.has(key) || key.startsWith('data-') || key.startsWith('aria-')) {
      out[key] = rest[key];
    }
  }
  return out;
}

export interface MetricDisplayProps extends StyledProps, UnsafeStyleProps, AllowedRest {
  value: string | number;
  size?: MetricSize;
  tint?: MetricTint;
}

function lengthBucket(v: string): 'short' | 'medium' | 'long' {
  if (v.length <= 6) return 'short';
  if (v.length <= 9) return 'medium';
  return 'long';
}

export const MetricDisplay = React.forwardRef<HTMLSpanElement, MetricDisplayProps>(
  function MetricDisplay({ value, size = 'large', tint, className, unsafe_style, ...rest }, ref) {
    const str = String(value);
    const len = lengthBucket(str);

    const restProps = pickAllowed(
      rest as Record<string, unknown>,
    ) as React.HTMLAttributes<HTMLSpanElement>;

    return (
      <span
        ref={ref}
        {...restProps}
        className={cx(
          'j-metric',
          `j-metric-${len}`,
          size === 'small' && 'j-metric-sm',
          tint && `j-metric-${tint}`,
          className,
        )}
        style={unsafe_style}
      >
        {str}
      </span>
    );
  },
);
